import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { OnboardingItemKey } from 'fym-shared/src/music24/onboardingChecklist';
import type { OnboardingStateAnswer } from '../../../../../music24-api/src/trpc/onboarding.router';
import { useTRPCAPI, useTRPCClientAPI } from '../../../trpc/trpc';
import { useUserTokenDecoded } from '../../../redux/slices/appInfo/hooks';
import { SMLAnalytics } from '../../analytics/sml/analytics';

/**
 * The keys that this page load already sent, for each user and artist. The card and the sidebar
 * pill both call the hook, and the answer keeps a key until `markReported` ends. Without this set
 * the second mount sent the event again.
 */
const sentKeys = new Map<string, Set<OnboardingItemKey>>();

function sentOf(userId: number, accountId: number): Set<OnboardingItemKey> {
  const mapKey = `${userId}:${accountId}`;
  let sent = sentKeys.get(mapKey);
  if (sent === undefined) {
    sent = new Set();
    sentKeys.set(mapKey, sent);
  }
  return sent;
}

/** Tests only. */
export function resetChecklistAnalytics(): void {
  sentKeys.clear();
}

/**
 * Sends `onboarding_item_done { key, accountId }` once for each artist and item (one checklist per
 * artist). The server keeps the reported keys of each artist: `onboarding.state.unreportedDone`
 * lists the done items with no event yet. The hook sends the event for each key and stores the keys
 * with `onboarding.markReported` for the same artist. So a new device, a sign-out or an empty
 * browser storage sends nothing again.
 *
 * `onboarding_checklist_complete` goes out with the report that ends the list: every item is done
 * and at least one of them is new in this report. After it `unreportedDone` stays empty.
 */
export function useChecklistAnalytics(state: OnboardingStateAnswer | undefined): void {
  const trpcAPI = useTRPCAPI();
  const trpcClient = useTRPCClientAPI();
  const queryClient = useQueryClient();
  const userId = useUserTokenDecoded()?.id ?? null;
  const unreportedDone = state?.unreportedDone;
  const isComplete = state?.checklist?.complete === true;
  const accountId = state?.checklistArtist?.accountId ?? null;

  useEffect(() => {
    if (userId === null || accountId === null || unreportedDone === undefined || unreportedDone.length === 0) {
      return;
    }
    const sent = sentOf(userId, accountId);
    const keys = unreportedDone.filter((key) => !sent.has(key));
    if (keys.length === 0) {
      return;
    }
    keys.forEach((key) => {
      sent.add(key);
      SMLAnalytics.track('onboarding_item_done', { key, accountId: `${accountId}` }).catch(console.error);
    });
    if (isComplete) {
      SMLAnalytics.track('onboarding_checklist_complete', { accountId: `${accountId}` }).catch(console.error);
    }
    const queryKey = trpcAPI.onboarding.state.queryKey();
    trpcClient.onboarding.markReported
      .mutate({ keys, accountId })
      .then(() => {
        // The keys are stored: take them out of the cached answers of this artist. No refetch is
        // necessary.
        queryClient.setQueriesData({ queryKey }, (old: OnboardingStateAnswer | undefined) =>
          old === undefined || old.checklistArtist?.accountId !== accountId
            ? old
            : { ...old, unreportedDone: old.unreportedDone.filter((key) => !keys.includes(key)) }
        );
      })
      // A failed call is silent. The keys stay sent for this page load; the next one reports again.
      .catch(console.error);
  }, [accountId, isComplete, queryClient, trpcAPI, trpcClient, unreportedDone, userId]);
}
